import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, ArrowLeft, AlertCircle, Heart, Briefcase, Globe, CloudLightning, ExternalLink, ShieldOff } from 'lucide-react';
import { SEO } from '../components/SEO';

function DisclaimerPage() {
  const navigate = useNavigate();

  const sections = [
    {
      num: '1', title: 'Physical Risk and Injury',
      bullets: [
        'Sports activities conducted at Malda Turf involve inherent risks of physical injury.',
        'Malda Turf, its owners, and employees shall not be held liable for any accidents, injuries, disabilities, or fatalities that occur on the premises.',
        'Players and spectators enter and use the facility at their own risk.',
        'It is the responsibility of the users to ensure they are medically fit and have the necessary skills to participate in the activities they book.',
      ]
    },
    {
      num: '2', title: 'Personal Property',
      text: 'Malda Turf is not responsible for the loss, theft, or damage of any personal belongings, including vehicles, mobile phones, sports gear, or valuables brought to the venue. Users are encouraged to keep their belongings secure at all times.'
    },
    {
      num: '3', title: 'Website Accuracy',
      text: 'While we strive to keep the information on maldaturf.in accurate and up-to-date, we make no guarantees regarding the completeness, reliability, or availability of the website or the information, products, or services contained on it. Any reliance you place on such information is strictly at your own risk.'
    },
    {
      num: '4', title: 'External Factors (Weather & Technical)',
      intro: 'Malda Turf shall not be held responsible for any interruptions in service caused by factors beyond our control, including but not limited to:',
      bullets: [
        'Extreme weather conditions or "Acts of God."',
        'Power outages or technical failures.',
        'Government restrictions or local disturbances.',
      ],
      note: 'In such events, our liability is limited strictly to the Rescheduling Policy outlined in our Refund Policy.'
    },
    {
      num: '5', title: 'Third-Party Links',
      text: 'Our website may contain links to third-party websites (such as payment gateways). We have no control over the nature, content, and availability of those sites. The inclusion of any links does not necessarily imply a recommendation or endorsement of the views expressed within them.'
    },
    {
      num: '6', title: 'No Warranties',
      text: 'The facility is provided on an "as-is" and "as-available" basis. Malda Turf makes no warranties, expressed or implied, regarding the suitability of the turf for specific professional standards unless explicitly stated.'
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title="Disclaimer"
        description="Read the Malda Turf disclaimer regarding physical risk, personal property, third-party links, and external factors."
        path="/disclaimer"
      />
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 bg-[#16A34A] flex items-center justify-center flex-shrink-0" style={{ borderRadius: '50px' }}>
              <Trophy className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-base font-bold tracking-tight font-heading">Malda Turf</span>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-white transition-colors font-body">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-[#16A34A]" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading">Disclaimer</h1>
                <p className="text-[11px] text-neutral-500 font-body mt-0.5">Last updated: April 2026</p>
              </div>
            </div>
            <p className="text-sm text-neutral-400 font-body leading-relaxed">
              The information and services provided by <a href="https://maldaturf.in" className="text-[#16A34A] hover:underline">Malda Turf</a> are for general informational purposes and facility booking only. By using this website or our physical facilities, you acknowledge and agree to the following:
            </p>
          </div>

          <div className="space-y-8">
            {sections.map((section) => (
              <div key={section.num} className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 md:p-8" data-testid={`disclaimer-section-${section.num}`}>
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#16A34A]/10 border border-[#16A34A]/20 flex items-center justify-center text-[11px] font-bold text-[#16A34A]">{section.num}</span>
                  <h2 className="text-base md:text-lg font-bold font-heading pt-0.5">{section.title}</h2>
                </div>
                <div className="pl-10">
                  {section.intro && <p className="text-sm text-neutral-400 font-body leading-relaxed mb-4">{section.intro}</p>}
                  {section.text && <p className="text-sm text-neutral-400 font-body leading-relaxed">{section.text}</p>}
                  {section.bullets && (
                    <ul className="space-y-2.5">
                      {section.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]/50 flex-shrink-0 mt-1.5"></div>
                          <span className="text-sm text-neutral-400 font-body leading-relaxed">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.note && (
                    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl p-4 mt-4">
                      <p className="text-sm text-neutral-300 font-body leading-relaxed">
                        {section.note.split('Refund Policy')[0]}
                        <button onClick={() => navigate('/refund')} className="text-[#16A34A] hover:underline font-medium">Refund Policy</button>
                        {section.note.split('Refund Policy')[1]}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-[11px] text-neutral-600 font-body">
              By using Malda Turf, you acknowledge and agree to the terms of this Disclaimer.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DisclaimerPage;
